define(function(require, exports, module) {
    'use strict';
    var AbstractTracker = require('modules/trackers/abstract.tracker');
    var core = require('core');
    var user = require('user');
    var { browserName, formFactor } = require('util/device.util');
    var config = require('config');
    var authManager = require('auth');
    var url = require('util/url.util');
    const { dispatcher, getDeviceId } = require('bundle');

    const deliveryPlatformFromUrl = url.getLocationParameterByName('deliveryPlatform');
    const deliveryPlatform = deliveryPlatformFromUrl && deliveryPlatformFromUrl.toLowerCase() === 'native';

    const userAgent = {
        platform: deliveryPlatform ? 'native' : formFactor,
        device: getDeviceId(),
        browser: browserName
    };

    const STATUS_VALUES = {
        FAILED: 'Failed',
        SUCCESS: 'Success'
    };

    function generateDepositEvent(event, additionalData) {
        window.dataLayer.push({
            event,
            advertiser: user.get('userDetails').advertiser,
            refererUrl: user.get('userDetails').refererUrl,
            bannerId: user.get('userDetails').bannerId,
            clientType: config.get('clientType'),
            creferer: user.get('userDetails').creferer,
            currency: user.get('currencyCode'),
            email: user.get('userDetails').email,
            phone: user.get('userDetails').cellphone,
            playerCode: user.get('userDetails').playerCode,
            playerTags: user.get('tags'),
            profileId: user.get('userDetails').profileId,
            userId: user.get('userDetails').userId,
            userName: user.get('userName'),
            url: window.location.pathname,
            ...userAgent,
            ...additionalData
        });
    }

    module.exports = AbstractTracker.extend({
        initialize: function() {
            if (authManager.isAdmin()) {
                return;
            }

            window.dataLayer = window.dataLayer || [];

            if (dispatcher) {
                dispatcher.listen('auth:credentials:login:failed', this.handleLoginFail);
                dispatcher.listen('auth:credentials:login', this.handleLoginSuccess);
                dispatcher.listen('auth:phone:verificationInit', this.generateSmsVerificationEvent);
                dispatcher.listen('auth:phone:verificationComplete', this.generateSmsVerificationEvent);
                dispatcher.listen('auth:action:logout', this.handleLogout);
                dispatcher.listen('category:open',this.handleGamesFeedsEvent('Category_Open'));
                dispatcher.listen('category:close',this.handleGamesFeedsEvent('Category_Closed'));
                dispatcher.listen('category:scroll:left',this.handleGamesFeedsEvent('Category_Scroll_Left'));
                dispatcher.listen('category:scroll:right',this.handleGamesFeedsEvent('Category_Scroll_Right'));
                dispatcher.listen('feed:open',this.handleGamesFeedsEvent('Feed_Open'));
                dispatcher.listen('games:search', this.handleSearch);
                dispatcher.listen('passkey:add', this.handleAddPasskey);
                dispatcher.listen('password:change', this.handleChangePassword);
            }

            this.listenTo(core, {
                'game:launch': this.handleGameLaunch,
                'game:bet-placed': this.handleGameBetPlaced,
                'cashier:opened': this.handleCashierOpened,
                'registration:opened': this.handleRegistrationOpened,
                'registration:success': this.handleRegistrationSuccess,
                'registration:error': this.handleRegistrationFail,
                'deposit:input:change': this.handleDepositInputChange,
                'deposit:click': this.handleDepositClick,
                'deposit:success': this.handleDepositSuccess,
                'deposit:fail': this.handleDepositFailed,
                'page:rendered': this.handlePageRendered,
                'cashier:pm:selection': this.handlePaymentMethodSelection,
                'cashier:pm:load': this.handlePaymentPageLoad,
                'cashier:pm:internalCreditCard': this.handlePaymentInternalCreditCard
            });
        },

        handleAddPasskey: function({ success, ...errorDetails }) {
            window.dataLayer.push({
                event: 'Add_Passkey',
                userId: user.get('userDetails').userId,
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode,
                status: success ? STATUS_VALUES.SUCCESS : STATUS_VALUES.FAILED,
                ...errorDetails,
            });
        },

        handleChangePassword: function({ success, ...errorDetails }) {
            window.dataLayer.push({
                event: 'Change_Password',
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode,
                status: success ? STATUS_VALUES.SUCCESS : STATUS_VALUES.FAILED,
                ...errorDetails
            });
        },

        handleGameLaunch: function(game, success, options) {
            const { source, feedName, categoryName } = options || {};
            const feedAdditionalData = source === 'feed' ? { feedName, categoryName: categoryName.trim() } : {};
            const gameItem = game.attributes || game;

            window.dataLayer.push({
                event: 'Casino_Game_Launched',
                launchResult: success ? 'success' : 'fail',
                source,
                ...feedAdditionalData,
                ...userAgent,
                userName: user.get('userName'),
                game: gameItem.name,
                sku: gameItem.gameCode,
                url: window.location.pathname
            });
        },

        handleGameBetPlaced: function() {
            window.dataLayer.push({
                event: 'Bet_Placed',
                userId: user.get('userDetails').userId,
                currency: user.get('currencyCode'),
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode
            });
        },

        handleCashierOpened: function() {
            window.dataLayer.push({
                event: 'Cashier_Open',
                url: window.location.pathname,
                userId: user.get('userDetails').userId,
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode
            });
        },

        handleRegistrationOpened: function() {
            window.dataLayer.push({
                event: 'Open_Registration'
            });
        },

        handleRegistrationSuccess: function() {
            window.dataLayer.push({
                event: 'Signup',
                status: STATUS_VALUES.SUCCESS,
                clientType: config.get('clientType'),
                email: user.get('formData').email,
                phone: user.get('formData').phone
            });
        },

        handleRegistrationFail: function() {
            window.dataLayer.push({
                event: 'Signup',
                status: STATUS_VALUES.FAILED,
                clientType: config.get('clientType')
            });
        },

        handleLoginSuccess: function() {
            window.dataLayer.push({
                event: user.get('previousLoginTime') ? 'Login' : 'FirstLogin',
                status: STATUS_VALUES.SUCCESS,
                url: window.location.pathname,
                playerTags: user.get('tags'),
                clientType: config.get('clientType'),
                userId: user.get('userDetails').userId,
                frozen: user.get('userDetails').frozen,
                vipLevel: user.get('userDetails').vipLevel,
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode,
                advertiser: user.get('userDetails').advertiser,
                bannerId: user.get('userDetails').bannerId,
                creferer: user.get('userDetails').creferer,
                profileId: user.get('userDetails').profileId,
                refererUrl: user.get('userDetails').refererUrl,
                email: user.get('userDetails').email,
                phone: user.get('userDetails').cellphone,
                verPhone: user.get('userDetails').mobilePhoneVerified,
            });
        },

        handleLoginFail: function() {
            window.dataLayer.push({
                event: 'Login',
                status: STATUS_VALUES.FAILED,
                url: window.location.pathname,
                clientType: config.get('clientType')
            });
        },

        handleLogout: function() {
            window.dataLayer.push({
                event: 'Logout',
                status: STATUS_VALUES.SUCCESS,
                url: window.location.pathname
            });
        },

        generateSmsVerificationEvent: function({ event, success, variant }) {
            window.dataLayer.push({
                event,
                status: success ? STATUS_VALUES.SUCCESS : STATUS_VALUES.FAILED,
                variant,
                userId: user.get('userDetails').userId,
                playerCode: user.get('userDetails').playerCode,
                userName: user.get('userName'),
                url: window.location.pathname,
            });
        },

        handleDepositInputChange: function({ buttonId, amount, vipLevel, methodCode }) {
            generateDepositEvent('Input_Field_Change', {
                buttonId, amount, vipLevel, methodCode
            });
        },

        handleDepositClick: function({ amount, methodCode }) {
            generateDepositEvent('Click_on_Deposito', {
                amount, methodCode
            });
        },

        handleDepositSuccess: function(data) {
            var transaction = data.payments && data.payments.transaction;
            var depositAmount = data.amount || transaction && transaction.length && transaction[0].amount;
            var totalDepositCount = user.get('paymentsStatistics').deposits.totalDepositCount;

            depositAmount = parseInt(depositAmount);
            totalDepositCount = parseInt(totalDepositCount) || 0;

            if (totalDepositCount === 1) {
                window.dataLayer.push({
                    event: 'FTD',
                    status: STATUS_VALUES.SUCCESS,
                    amount: parseInt(depositAmount),
                    clientType: config.get('clientType'),
                    methodCode: data.methodCode || data.type, // methodCode for non-redirect PM and type for redirect PM
                    currency: user.get('currencyCode'),
                    playerTags: user.get('tags'),
                    userId: user.get('userDetails').userId,
                    userName: user.get('userName'),
                    playerCode: user.get('userDetails').playerCode,
                    advertiser: user.get('userDetails').advertiser,
                    bannerId: user.get('userDetails').bannerId,
                    creferer: user.get('userDetails').creferer,
                    profileId: user.get('userDetails').profileId,
                    refererUrl: user.get('userDetails').refererUrl,
                    email: user.get('userDetails').email,
                    phone: user.get('userDetails').cellphone
                });
            } else if (totalDepositCount >= 2) {
                window.dataLayer.push({
                    event: 'SDeposit',
                    status: STATUS_VALUES.SUCCESS,
                    amount: parseInt(depositAmount),
                    clientType: config.get('clientType'),
                    methodCode: data.methodCode || data.type, // methodCode for non-redirect PM and type for redirect PM
                    currency: user.get('currencyCode'),
                    playerTags: user.get('tags'),
                    userId: user.get('userDetails').userId,
                    userName: user.get('userName'),
                    playerCode: user.get('userDetails').playerCode,
                    advertiser: user.get('userDetails').advertiser,
                    bannerId: user.get('userDetails').bannerId,
                    creferer: user.get('userDetails').creferer,
                    profileId: user.get('userDetails').profileId,
                    refererUrl: user.get('userDetails').refererUrl
                });
            }
        },

        handleDepositFailed: function(data) {
            var totalDepositCount = user.get('paymentsStatistics').deposits.totalDepositCount;

            totalDepositCount = parseInt(totalDepositCount) || 0;

            window.dataLayer.push({
                event: totalDepositCount > 1 ? 'SDeposit' : 'FTD',
                status: STATUS_VALUES.FAILED,
                amount: parseInt(data.amount),
                clientType: config.get('clientType'),
                methodCode: data.methodCode || data.type, // methodCode for non-redirect PM and type for redirect PM
                currency: user.get('currencyCode'),
                playerTags: user.get('tags'),
                userId: user.get('userDetails').userId,
                userName: user.get('userName'),
                playerCode: user.get('userDetails').playerCode,
                advertiser: user.get('userDetails').advertiser
            });
        },

        handlePageRendered: function() {
            if (['/yak', '/casino', '/casino-en-vivo'].includes(window.location.pathname)) {
                window.dataLayer.push({
                    event: 'Lobby_Open',
                    url: window.location.pathname,
                    userName: user.get('userName'),
                    playerCode: user.get('userDetails').playerCode,
                    userId: user.get('userDetails') && user.get('userDetails').userId
                });
            }
        },

        handleGamesFeedsEvent(event) {
            return ({ feed, categoryName }) => {
                window.dataLayer.push({
                    event,
                    feed,
                    url: window.location.pathname,
                    userName: user.get('userName'),
                    ...userAgent,
                    ...categoryName && { categoryName: categoryName.trim() }
                });
            };
        },

        handleSearch: function(data) {
            window.dataLayer.push({
                event: 'Search',
                searchTerm: data.searchTerm,
                url: window.location.pathname,
                userName: user.get('userName'),
                ...userAgent
            });
        },

        handlePaymentMethodSelection: function(methodCode) {
            generateDepositEvent('PM_Selection', {
                methodCode
            });
        },

        handlePaymentPageLoad: function(methodCode) {
            generateDepositEvent('Payment_Page_Load', {
                methodCode
            });
        },

        handlePaymentInternalCreditCard: function(methodCode) {
            generateDepositEvent('Payment_Internal_creditCard', {
                methodCode
            });
        }
    });
});
